import { useCallback, useContext, useRef } from 'react';
import { useLocation } from 'react-router';

import { menuData } from '@/layout/menu/components/data';

import { RightSectionContext } from '../../context/right-section';
import { buildSyncModules, moduleKeyForPath } from './help-modules';
import { HC_COPY } from './HelpCentre.Copy';

/** Derived once: the module list only changes with a deploy. Menu modules plus the general ones. */
export const HELP_MODULES = buildSyncModules(menuData);

export interface IHelpContextData {
	pathname: string;
	moduleKey: string | null;
	pageTitle?: string;
	/** The page's own one-line description — kept visible in the panel now that the hover tooltip is gone. */
	description?: string;
	/** Open straight onto one guide. */
	slug?: string;
	/** Stamp per open, so pressing "?" again on the same page resets the panel to its list. */
	openedAt?: number;
}

/**
 * Opens the contextual "Help for this page" panel. Returns `null` when there is no right panel
 * mounted (the shared test renderer, Storybook) — callers fall back rather than render a dead "?".
 *
 * Reads the context with `useContext`, not `useRightPanelContext()`, which THROWS without a provider.
 */
export const useOpenHelp = () => {
	const rightPanel = useContext(RightSectionContext);
	const { pathname } = useLocation();
	// The context value is a new object every render; hold it in a ref so `open` stays stable.
	const panelRef = useRef(rightPanel);
	panelRef.current = rightPanel;

	const open = useCallback(
		(extra: Omit<Partial<IHelpContextData>, 'pathname'> = {}) => {
			const data: IHelpContextData = {
				pathname,
				moduleKey: extra.moduleKey ?? moduleKeyForPath(pathname, HELP_MODULES),
				pageTitle: extra.pageTitle,
				description: extra.description,
				slug: extra.slug,
				openedAt: Date.now(),
			};
			panelRef.current?.callSection({
				action: 'view',
				component: 'help-context',
				title: HC_COPY.context.panelTitle,
				data,
				max: true,
			});
		},
		[pathname]
	);

	return rightPanel ? open : null;
};
